import type { FunctionComponent } from 'react';
import React from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import classnames from 'classnames';

import type { Direction } from '../../steps/types/step';
import { Loader } from '../loader/Loader';
import type { AppData } from '../../types/app_data';
import { isGarageFlow } from '../../utils/garage';

import { TopNav } from './components/top_nav/TopNav';
import { Footer } from './components/footer/Footer';
import { BodyContainer } from './components/body_container/BodyContainer';

export interface AppContainerProps {
  appData: AppData;
  className?: string;
  route: string;
  direction: Direction;
  isLoading: boolean;
  noShowNav?: boolean;

  // Header props
  jerryLogoGrey?: boolean;
  subHeaderText?: string;
  partnerLogo?: JSX.Element;
  logosSameLine?: boolean;
  mainPartnerLogo?: boolean;
  topNavbackgroundWhite?: boolean;

  // Body props
  fullBodyWidth?: boolean;
  unsetMax?: boolean;

  // Footer props
  noShowFooterLogo?: boolean;
  footerLogoGrey?: boolean;
  noShowFooter?: boolean;
  appendFooter?: JSX.Element; // Content to append to original footer.
  footer?: JSX.Element; // Replace whole footer
  loadingText?: string; // Used for show loading text below loading icon
}

/**
 * Container for the the app.
 * Basically just adds styling common to each page.
 */
const UnstyledAppContainer: FunctionComponent<AppContainerProps> = ({
  appData,
  className,
  route,
  direction,
  children,
  isLoading,
  noShowNav,
  jerryLogoGrey,
  subHeaderText,
  partnerLogo,
  logosSameLine,
  mainPartnerLogo,
  topNavbackgroundWhite,
  noShowFooterLogo,
  footerLogoGrey,
  fullBodyWidth,
  unsetMax,
  noShowFooter,
  appendFooter,
  footer,
  loadingText,
}) => {
  return (
    <div className={classnames(className, 'global-app-container')}>
      {!noShowNav && (
        <TopNav
          appData={appData}
          jerryLogoGrey={jerryLogoGrey}
          subHeaderText={subHeaderText}
          partnerLogo={partnerLogo}
          logosSameLine={logosSameLine}
          mainPartnerLogo={mainPartnerLogo}
          topNavbackgroundWhite={topNavbackgroundWhite}
        />
      )}

      {/* Wrap the body in `AnimationPresence` to retain animation state between page changes. */}
      <AnimatePresence initial={false} custom={direction}>
        {/* This motion div injects the `variants` used by subcomponents. */}
        <motion.div
          className={'bodyContainer'}
          key={route}
          custom={direction}
          variants={{
            enter: { position: 'absolute', left: 0, right: 0, opacity: 0 },
            center: { position: 'relative', opacity: 1 },
            exit: { position: 'absolute', left: 0, right: 0, opacity: 0, minHeight: '100vh' },
          }}
          transition={{ opacity: { duration: 0.5 } }}
          initial="enter"
          animate="center"
          exit="exit"
        >
          <BodyContainer fullBodyWidth={fullBodyWidth} unsetMax={unsetMax} topAlign={isGarageFlow(appData)}>
            {!isLoading ? children : <Loader loadingText={loadingText} />}
          </BodyContainer>
        </motion.div>
      </AnimatePresence>

      {!noShowFooter && (
        <Footer
          className="footer"
          noShowFooterLogo={noShowFooterLogo}
          footerLogoGrey={footerLogoGrey}
          appendFooter={appendFooter}
          footer={footer}
          appData={appData}
        />
      )}
    </div>
  );
};

export const AppContainer = styled(UnstyledAppContainer)`
  margin-top: inherit;
  position: relative;

  & .bodyContainer {
    display: flex;
  }
`;
